import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { useConsumableMutations } from '../hooks/useConsumables';
import { useLocation } from '@/contexts/LocationContext';
import type { ConsumableType, StockAdjustFormData } from '../types';

interface StockAdjustModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consumableType: ConsumableType;
  mode: 'add' | 'consume';
}

const emptyForm: StockAdjustFormData = {
  quantity: '',
  notes: '',
};

export default function StockAdjustModal({
  open,
  onOpenChange,
  consumableType,
  mode,
}: StockAdjustModalProps) {
  const { addStock, consumeStock } = useConsumableMutations();
  const { selectedLocation } = useLocation();
  const [formData, setFormData] = useState<StockAdjustFormData>(emptyForm);

  const quantityNum = parseFloat(formData.quantity) || 0;
  const isAdd = mode === 'add';
  const isLoading = addStock.isPending || consumeStock.isPending;

  const costPreview =
    consumableType.unit_cost != null && quantityNum > 0
      ? quantityNum * consumableType.unit_cost
      : null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const mutation = isAdd ? addStock : consumeStock;

    mutation.mutate(
      {
        consumableTypeId: consumableType.id,
        quantity: quantityNum,
        notes: formData.notes.trim() || undefined,
        locationId: selectedLocation?.id,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setFormData(emptyForm);
        },
      }
    );
  };

  const handleOpenChange = (val: boolean) => {
    onOpenChange(val);
    if (!val) setFormData(emptyForm);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {isAdd ? 'Add Stock' : 'Consume Stock'} - {consumableType.name}
          </DialogTitle>
          <DialogDescription>
            {isAdd
              ? 'Record stock received or added to inventory.'
              : 'Record stock used or consumed.'}
            {selectedLocation && (
              <span className="block mt-1 font-medium text-foreground">
                Location: {selectedLocation.name}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Current stock info */}
            <div className="rounded-md bg-muted p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current Stock</span>
                <span className="font-medium">
                  {consumableType.stock_on_hand} {consumableType.unit}
                </span>
              </div>
              {consumableType.unit_cost != null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unit Cost</span>
                  <span className="font-medium">
                    ${consumableType.unit_cost.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sa-qty">
                Quantity ({consumableType.unit}) *
              </Label>
              <Input
                id="sa-qty"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                required
                disabled={isLoading}
                autoFocus
              />
            </div>

            {costPreview != null && (
              <div className="rounded-md border p-3 flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {isAdd ? 'Cost Value' : 'COGS Value'}
                </span>
                <span className="text-lg font-semibold">
                  ${costPreview.toFixed(2)}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="sa-notes">Notes</Label>
              <Textarea
                id="sa-notes"
                placeholder={
                  isAdd
                    ? 'e.g., Received from supplier order #1234'
                    : 'e.g., Used for production run'
                }
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                disabled={isLoading}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || quantityNum <= 0}
              variant={isAdd ? 'default' : 'destructive'}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isAdd ? 'Adding...' : 'Consuming...'}
                </>
              ) : isAdd ? (
                `Add ${quantityNum > 0 ? quantityNum : ''} ${consumableType.unit}`
              ) : (
                `Consume ${quantityNum > 0 ? quantityNum : ''} ${consumableType.unit}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
